// analytics.ts — kontrakt analityczny CRM widziany od strony Control Center.
//
// Typy są spisane z realnych odpowiedzi backendu (crm/backend/routes/analytics.js), a nie wymyślone.
// Kwoty przychodzą w GROSZACH — nazwa pola zawsze to mówi. Zamiana na złote następuje wyłącznie
// w warstwie prezentacji; żadne obliczenie nie dzieje się na liczbach zmiennoprzecinkowych złotówek.

import type { ApiClient } from './client';

export interface SalesRangeMeta {
  date_from: string;
  date_to: string;
  city?: string | null;
}

export interface SchoolSalesRow {
  /** BIGINT w PostgreSQL → sterownik oddaje ŁAŃCUCH. Typ mówi prawdę, normalizacja w joinKey(). */
  school_id: number | string;
  school_name: string | null;
  city: string | null;
  period_sales_grosze: number;
  period_orders: number;
  period_days: number;
  period_expected_days: number;
}

/**
 * Punkt serii dziennej. Oś czasu to DZIEŃ DOSTAWY (ADR-003) — backend kubełkuje po
 * `delivery_date`, nie po dacie zakupu. Kwota to wartość wg cen katalogowych (ADR-001).
 */
export interface DailyTrendPoint {
  date: string;
  sales_grosze: number;
  orders: number;
}

/** Pozycja słownika do selecta. Backend zwracał to od początku; klient tego nie deklarował. */
export interface SchoolOption {
  school_id: number;
  school_name: string | null;
  city: string | null;
}

export interface SchoolSalesResponse {
  range: SalesRangeMeta;
  count: number;
  summary: { total_sales_grosze: number; total_orders: number; schools_count: number };
  schools: SchoolSalesRow[];
  /**
   * Lista WSZYSTKICH aktywnych szkół — niezależna od filtra i od tego, czy szkoła sprzedała
   * cokolwiek w oknie. Backend liczył ją od początku, a klient jej nie odbierał.
   */
  school_options?: SchoolOption[];
  /**
   * Wykresy. `daily_trend` był liczony po stronie backendu i WYRZUCANY, bo nie istniał
   * w kontrakcie klienta — pole dopisane w R5-15-006 (bezpieczna wygrana parytetu A).
   */
  charts?: {
    /** Projekcja `slim` z backendu — NIE pełny wiersz listy. Patrz SlimChartRow. */
    top_schools: SlimChartRow[];
    bottom_schools: SlimChartRow[];
    daily_trend: DailyTrendPoint[];
  };
}

/**
 * Wiersz OKROJONY, wysyłany wyłącznie w sekcji `charts`.
 *
 * To NIE jest ten sam kształt co pełny wiersz listy — i to jest pułapka, która przez chwilę
 * siedziała w tym pliku. Kontrakt deklarował tu `SchoolSalesRow[]` / `RestaurantSalesRow[]`,
 * podczas gdy backend (crm/backend/routes/analytics.js, funkcja `slim`) wysyła własną projekcję:
 * inne NAZWY pól (`sales_grosze` zamiast `period_sales_grosze`, `orders` zamiast `period_orders`)
 * i brak reszty. Skutek był podstępny: kod czytający `charts.top_schools[i].period_sales_grosze`
 * dostawał `undefined`, formatter zamieniał to na „—", a TypeScript nie zgłaszał NICZEGO,
 * bo typ obiecywał pole, którego w odpowiedzi nigdy nie było.
 *
 * Zły typ jest gorszy niż brak typu: brak typu zmusza do sprawdzenia, zły typ zwalnia ze
 * sprawdzania i myli się cicho.
 */
export interface SlimChartRow {
  school_id?: number;
  restaurant_id?: number;
  school_name?: string | null;
  restaurant_name?: string | null;
  city: string | null;
  /** Wartość w oknie. Uwaga na nazwę — w pełnym wierszu to `period_sales_grosze`. */
  sales_grosze: number;
  /** Liczba zamówień w oknie. W pełnym wierszu: `period_orders`. */
  orders: number;
}

export interface RestaurantSalesRow {
  restaurant_id: number;
  restaurant_name: string | null;
  city: string | null;
  period_sales_grosze: number;
  period_orders: number;
  period_days: number;
}

export interface RestaurantOption {
  restaurant_id: number;
  restaurant_name: string | null;
  city: string | null;
}

export interface RestaurantSalesResponse {
  range: SalesRangeMeta;
  count: number;
  summary: { total_sales_grosze: number; total_orders: number; restaurants_count: number };
  restaurants: RestaurantSalesRow[];
  /** Komplet aktywnych restauracji — niezależny od filtra i od sprzedaży w oknie. */
  restaurant_options?: RestaurantOption[];
  charts?: {
    /** Projekcja `slim` z backendu — NIE pełny wiersz listy. Patrz SlimChartRow. */
    top_restaurants: SlimChartRow[];
    bottom_restaurants: SlimChartRow[];
    daily_trend: DailyTrendPoint[];
  };
}

/** Jakość danych — ta sama skala co w silniku po stronie backendu. */
export type BackendDataQuality = 'GOOD' | 'PARTIAL' | 'LOW_SAMPLE' | 'MISSING_HISTORY' | 'UNRELIABLE_JOIN' | 'UNKNOWN';

export interface DishAggregate {
  /** Liczba POZYCJI zamówienia. System główny zapisuje jedną sztukę na pozycję. */
  units: number;
  /** SUM(qty) — wyłącznie kontrola spójności z `units`; rozjazd obniża jakość danych. */
  qty_sum: number;
  /**
   * Wartość w cenach KATALOGOWYCH (OrderPosition.Amount), przed zniżką szkolną.
   * To NIE jest kwota pobrana od klienta — nazwa pola celowo o tym mówi.
   */
  revenue_list_grosze: number;
  orders: number;
  buyers: number;
  days: number;
  schools: number;
  restaurants: number;
  unit_price_grosze: number | null;
}

export interface DishSalesRow {
  meal_id: number | null;
  meal_name: string | null;
  current: DishAggregate;
  previous: DishAggregate;
  units_delta_pct: number | null;
  revenue_delta_pct: number | null;
  unit_price_delta_pct: number | null;
  repeat_buyers: number;
  repeat_ratio: number | null;
  revenue_share_pct: number | null;
  data_quality: BackendDataQuality;
  data_quality_reason: string;
}

export type InsightType =
  | 'TOP_DISH'
  | 'FASTEST_GROWING_DISH'
  | 'FASTEST_DECLINING_DISH'
  | 'LOW_PERFORMER'
  | 'PRICE_CHANGE_SIGNAL'
  | 'HIGH_REPEAT_PRODUCT'
  | 'SCHOOL_OUTLIER';

/**
 * Deklaracja rankingu — obowiązkowa dla każdego insightu, który TWIERDZI O SKRAJNOŚCI.
 *
 * Zdanie „największy spadek" jest sprawdzalne dopiero wtedy, gdy wiadomo: największy WEDŁUG
 * CZEGO i W JAKIM ZBIORZE. Bez tego walidator musiałby zgadywać metrykę z prozy — a zgadywanie
 * z prozy jest dokładnie tym, przed czym ten kontrakt chroni.
 *
 * `null` znaczy: ten insight nie twierdzi o skrajności. NIE znaczy „nie wiadomo".
 */
export interface DeklaracjaRankingu {
  /** Metryka, po której backend faktycznie posortował. Nie opis, tylko klucz. */
  metric: string;
  /** MAX — „największy"; MIN — „najmniejszy"/„największy spadek". */
  direction: 'MAX' | 'MIN';
  /** Zbiór, w którym byt jest skrajny. Superlatyw bez zbioru nie znaczy nic. */
  source: string;
}

export interface Insight {
  insight_id: string;
  type: InsightType;
  entity_type: string;
  entity_id: number | null;
  entity_name: string | null;
  title: string;
  fact: string;
  why_it_matters: string;
  recommended_next_step: string;
  evidence: Record<string, number | null>;
  metric_values: Record<string, number | null>;
  time_window: { date_from: string; date_to: string };
  comparison_window: { date_from: string; date_to: string };
  sample_size: number;
  data_quality: BackendDataQuality;
  /** Patrz DeklaracjaRankingu. null = insight nie twierdzi o skrajności. */
  ranking?: DeklaracjaRankingu | null;
  hypothesis: string | null;
  suggested_test: string | null;
  /** Backend deklaruje to w każdym insighcie. Jeśli kiedykolwiek przyjdzie co innego — to jest błąd. */
  causation: 'NOT_ESTABLISHED';
  engine_version: string;
}

/** Szkoła, w której udział dania jest wyraźnie niższy niż mediana pozostałych szkół restauracji. */
export interface SchoolOutlier {
  meal_id: number;
  meal_name: string | null;
  school_id: number;
  school_name: string | null;
  units: number;
  school_total_units: number;
  share: number;
  median_share_other_schools: number;
  share_ratio: number;
  other_schools: number;
}

export interface DishSalesResponse {
  range: { date_from: string; date_to: string; comparison: { date_from: string; date_to: string }; days: number };
  count: number;
  returned: number;
  summary: { total_revenue_list_grosze: number; total_units: number; dishes_count: number };
  dishes: DishSalesRow[];
  insights: Insight[];
  school_outliers: SchoolOutlier[];
  engine_version: string;
  caveats: string[];
}

export interface SalesQuery {
  date_from?: string;
  date_to?: string;
  city?: string;
  restaurant_id?: number;
  school_id?: number;
  top_n?: number;
}

export function createAnalyticsApi(client: ApiClient) {
  return {
    schoolSales: (q: SalesQuery = {}) =>
      client.get<SchoolSalesResponse>('crm', '/api/crm/analytics/school-sales', { ...q }),
    restaurantSales: (q: SalesQuery = {}) =>
      client.get<RestaurantSalesResponse>('crm', '/api/crm/analytics/restaurant-sales', { ...q }),
    dishSales: (q: SalesQuery = {}) =>
      client.get<DishSalesResponse>('crm', '/api/crm/analytics/dish-sales', { ...q }),

    // O1 — rejestr pojedynczej platnosci. Osobna rodzina, bo inne ziarno i inne ryzyko.
    //
    // `/api/crm/transactions` to DEDYKOWANY router `crm/backend/routes/transactions.js`
    // (server.js:102), a nie trasa o tej nazwie w `read.js` — tamta jest ZASŁONIĘTA, bo goły
    // `/api/crm` montuje się dopiero na końcu (server.js:132). Różnica jest istotna: zasłonięta
    // trasa dołącza `crm_customers` po imię i e-mail, dedykowany router świadomie tego nie robi
    // i zwraca `buyer_identifier_safe`. To ten drugi odpowiada i to on jest kontraktem CC.
    transactions: (q: TransactionQuery = {}) =>
      client.get<TransactionListResponse>('crm', '/api/crm/transactions', { ...q }),
    transactionDetail: (id: number) =>
      client.get<TransactionDetail>('crm', `/api/crm/transactions/${id}`),

    // Katalog dan — REJESTR. Danie bez sprzedazy tu JEST.
    dishCatalog: (q: { restaurant_id?: number; search?: string } = {}) =>
      client.get<DishCatalogResponse>('crm', '/api/crm/dictionary/dishes', { ...q }),

    // Słowniki REFERENCYJNE. Osobne od wymiarów sprzedaży o tych samych nazwach: tamte mówią,
    // w jakich szkołach coś KUPIONO, te — jakie szkoły w ogóle istnieją.
    schoolsDictionary: () =>
      client.get<SchoolsDictionaryResponse>('crm', '/api/crm/dictionary/schools'),
    restaurantsDictionary: () =>
      client.get<RestaurantsDictionaryResponse>('crm', '/api/crm/dictionary/restaurants'),
  };
}

export type AnalyticsApi = ReturnType<typeof createAnalyticsApi>;

// ── O1: rejestr pojedynczej płatności ────────────────────────────────────────
// Kontrakt świadomie NIE zawiera imienia, nazwiska, e-maila ani telefonu kupującego.
// Do odnalezienia płatności przy reklamacji wystarcza identyfikator (minimum konieczne).

export interface TransactionListRow {
  transaction_id: number;
  order_id: number | null;
  created_at: string | null;
  order_date: string | null;
  fulfillment_date: string | null;
  historical_school_id: number | null;
  historical_school_name: string | null;
  restaurant_id: number | null;
  restaurant_name: string | null;
  buyer_identifier_safe: string | null;
  status: string | null;
  transaction_type: number | null;
  /** Suma cen KATALOGOWYCH (przed zniżką szkolną). NIE jest to kwota, która wpłynęła. */
  list_value_total_grosze: number | null;
  /** Kwota FAKTYCZNIE POBRANA. `null` = nie wiemy, NIGDY nie znaczy zero. */
  paid_amount_grosze: number | null;
  /** Różnica — wyłącznie gdy obie kwoty są znane. Inaczej `null`. */
  discount_difference_grosze: number | null;
  missing_fields: string[];
}

export interface TransactionListResponse {
  count: number;
  total: number;
  limit: number;
  truncated: boolean;
  transactions: TransactionListRow[];
}

export interface TransactionItem {
  order_item_id: number;
  dish_id: number | null;
  /** Nazwa z katalogu, jeśli danie w nim jest; inaczej snapshot z zamówienia. */
  dish_name: string | null;
  /** Nazwa zapisana w chwili zakupu — katalog mógł ją później zmienić. */
  dish_name_snapshot: string | null;
  quantity: number | null;
  historical_list_price_grosze: number | null;
  line_list_value_grosze: number | null;
  restaurant_id: number | null;
  is_deleted: boolean;
  /** ZAWSZE null — źródło nie zna kwoty pobranej na ziarnie pozycji. */
  paid_amount_grosze: null;
  paid_amount_note: string;
}

export interface TransactionDetail extends TransactionListRow {
  order_items: TransactionItem[];
  order_items_count: number;
  caveats: string[];
}

export interface TransactionQuery {
  date_from?: string;
  date_to?: string;
  school_id?: number;
  restaurant_id?: number;
  status?: string;
  transaction_id?: number;
  limit?: number;
}

// ── Katalog dań ──────────────────────────────────────────────────────────────

export interface CatalogDish {
  dish_id: number;
  restaurant_id: number | null;
  restaurant_name: string | null;
  name: string | null;
  description: string | null;
  ingredients: string | null;
  list_price_grosze: number | null;
  is_active: boolean;
  first_seen_at: string | null;
  last_seen_at: string | null;
  source: string | null;
  order_item_rows: number;
  /** Danie bez sprzedaży ZOSTAJE w katalogu i jest tak oznaczone. */
  has_sales: boolean;
}

/**
 * Proweniencja ODCZYTU, z którego powstał katalog — nie zapisu.
 * Bez niej ekran umie pokazać wynik (13 dań) i nie ma czym wyjaśnić, że stoi za nim 364 wiersze
 * menu dziennego. „Liczba dań" mylona z „liczbą wierszy karty" to najkosztowniejsze
 * nieporozumienie w tej domenie: karta jest KOPIOWANA na kolejne dni.
 */
export interface CatalogSourceMeta {
  zrodlo: string;
  baza: string;
  /** Data karty w kodowaniu źródła: rok*100000 + miesiąc*100 + dzień. */
  data_karty: number[];
  szkol_w_zrodle: number;
  szkol_z_menu: number;
  szkol_bez_menu: number;
  szkol_nieodczytanych: number;
  surowe_wystapienia_menu: number;
  unikalnych_dan: number;
  restauracji: number;
  regula_deduplikacji: string;
  /** Zakres, jaki to źródło NAPRAWDĘ pokrywa — osobno dla każdego słownika. */
  zakres_szkol?: ZakresZrodla;
  zakres_restauracji_kod?: ZakresZrodla;
  zakres_dan?: ZakresZrodla;
  /** Opis słowny ograniczenia zakresu restauracji (zostaje dla czytelności dowodu). */
  zakres_restauracji?: string;
  /** Szkoły, których NIE DAŁO SIĘ odczytać — to co innego niż „nie mają dziś karty". */
  szkoly_nieodczytane_lista?: { id: number; name: string | null; powod: string }[];
}

/**
 * ZAKRES ŹRÓDŁA — pole, którego brak pozwalał czytać częściowy słownik jak pełne dane wzorcowe.
 *
 * Publiczne API Main TEST ma dokładnie dwa wejścia: `/cities` i `/schools/{id}/meals`. Listy
 * restauracji NIE MA. Dlatego słownik szkół jest kompletny w rozumieniu katalogu publicznego,
 * a słownik restauracji zawiera WYŁĄCZNIE te, które wystąpiły w karcie z dnia odczytu — i nie
 * wolno go nazywać rejestrem restauracji TuSzamy.
 */
export type ZakresZrodla =
  | 'WSZYSTKIE_SZKOLY_KATALOGU_PUBLICZNEGO'
  | 'RESTAURACJE_Z_BIEZACEJ_KARTY'
  | 'BIEZACY_KATALOG_DAN'
  | 'NIEUSTALONY';

export interface ReferenceSchool {
  school_id: number | null;
  name: string | null;
  city: string | null;
  address: string | null;
  break_time: string | null;
  is_blocked: boolean;
  synced_at: string | null;
}

export interface ReferenceRestaurant {
  restaurant_id: number | null;
  name: string | null;
  city: string | null;
  phone: string | null;
  is_blocked: boolean;
  synced_at: string | null;
}

export interface DictionarySync {
  synced_at: string;
  upserted: number;
  source_meta: CatalogSourceMeta | null;
}

export interface SchoolsDictionaryResponse {
  count: number;
  blocked_count: number;
  source: string;
  dictionary_sync?: DictionarySync | null;
  schools: ReferenceSchool[];
}

export interface RestaurantsDictionaryResponse {
  count: number;
  blocked_count: number;
  source: string;
  dictionary_sync?: DictionarySync | null;
  restaurants: ReferenceRestaurant[];
}

export interface DishCatalogResponse {
  /**
   * Czy tabela katalogu w ogóle istnieje w tym środowisku. Na produkcji `crm_dishes` NIE
   * istnieje i nie ma decyzji, żeby ją zakładać — więc `false` jest tu stanem poprawnym,
   * nie awarią. Trzy różne zdania, których nie wolno mylić:
   *   catalog_available === false → katalogu nie ma gdzie czytać,
   *   count === 0                 → katalog jest, ale pusty,
   *   odrzucone żądanie           → odczyt się nie udał, warto spróbować ponownie.
   */
  catalog_available?: boolean;
  caveats?: string[];
  count: number;
  without_sales_count: number;
  /**
   * Liczba wierszy w CAŁYM rejestrze sprzedaży. Rozstrzyga, czy „danie bez sprzedaży" jest
   * pomiarem, czy tylko konsekwencją pustego źródła — te dwie sytuacje mają identyczną liczbę.
   */
  sales_source_rows?: number;
  sales_source_empty?: boolean;
  catalog_sync?: { synced_at: string; upserted: number; source_meta: CatalogSourceMeta | null } | null;
  source: string;
  dishes: CatalogDish[];
}
